import ErrorHandler from "../error-handlers/axios/defaultErrorHandler";

/**
 * @description Get all cards
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {object} params - Query parameters
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<Array>} Array of cards
 */
export const getCards = async (axiosInstance, params = {}, options) => {
  try {
    const res = await axiosInstance.get(`/cards`, { 
      ...options,
      params: { ...options?.params, ...params }
    });
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Get cards by section
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} sectionId
 * @param {object} params - Query parameters
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<Array>} Array of cards for the section
 */
export const getCardsBySection = async (axiosInstance, sectionId, params = {}, options) => {
  try {
    const res = await axiosInstance.get(`/cards/section/${sectionId}`, {
      ...options,
      params: { ...options?.params, ...params }
    });
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Get a single card
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} cardId
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Card data
 */
export const getCard = async (axiosInstance, cardId, options) => {
  try {
    const res = await axiosInstance.get(`/cards/${cardId}`, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Create a new card
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {object} payload - Card data
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Created card
 */
export const createCard = async (axiosInstance, payload, options) => {
  try {
    const res = await axiosInstance.post(`/cards`, payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Update a card
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} cardId
 * @param {object} payload - Updated card data
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<object>} Updated card
 */
export const updateCard = async (axiosInstance, cardId, payload, options) => {
  try {
    const res = await axiosInstance.patch(`/cards/${cardId}`, payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Delete a card
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} cardId
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<void>}
 */
export const deleteCard = async (axiosInstance, cardId, options) => {
  try {
    await axiosInstance.delete(`/cards/${cardId}`, options);
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
};

/**
 * @description Reorder cards in a section
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {string} sectionId
 * @param {object} payload - Cards with new order
 * @param {Array} payload.cards - Array of {id, order} objects
 * @param {import("axios").AxiosRequestConfig?} options 
 * @returns {Promise<void>}
 */
export const reorderCards = async (axiosInstance, sectionId, payload, options) => {
  try {
    const res = await axiosInstance.put(`/sections/${sectionId}/cards/reorder`, payload, options);
    return res?.data;
  } catch (error) {
    ErrorHandler.handle(error);
    throw error;
  }
}; 